import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useToast } from '@chatsdet/ui';
import { API_URL } from '../../../../config';
import { toApiError } from '../../../../lib/api-error';
import { toUrlId } from '../../../../lib/prefixed-ids';
import type {
  ConvergenceMergePreview,
  WorkflowForkLineage,
} from '../components/ForkLineageControls';

type UseWorkflowForkLineageArgs = {
  workflowId: string | null;
  forkedFromWorkflowId: string | null;
  forkedFromName: string | null;
  isSuperadmin: boolean;
};

export function useWorkflowForkLineage({
  workflowId,
  forkedFromWorkflowId,
  forkedFromName,
  isSuperadmin,
}: UseWorkflowForkLineageArgs): WorkflowForkLineage | null {
  const navigate = useNavigate();
  const { toast } = useToast();
  const qc = useQueryClient();

  const handleOpenOrigin = React.useCallback(() => {
    if (!forkedFromWorkflowId) return;
    navigate({
      to: '/flows/$workflowId',
      params: { workflowId: toUrlId('wf', forkedFromWorkflowId) },
    });
  }, [forkedFromWorkflowId, navigate]);

  const suggestToOriginalMutation = useMutation({
    mutationFn: async () => {
      if (!workflowId) throw new Error('No workflow ID');
      const r = await fetch(`${API_URL}/workflows/${workflowId}/suggest-to-original`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!r.ok) throw await toApiError(r, 'Failed to suggest changes');
      return r.json();
    },
    onSuccess: () => {
      toast.success(`Suggested changes to ${forkedFromName ?? 'the original flow'}`);
      qc.invalidateQueries({ queryKey: ['workflow-proposals'] });
    },
    onError: (err: Error) => toast.error(err.message || 'Could not suggest changes'),
  });

  const convergencePreviewQ = useQuery<ConvergenceMergePreview | null>({
    queryKey: ['workflow-convergence-merge-preview', workflowId],
    queryFn: async () => {
      if (!workflowId) return null;
      const r = await fetch(`${API_URL}/workflows/${workflowId}/convergence-merge-preview`, {
        credentials: 'include',
      });
      // Ordinary forks do not have a convergence run. Treat the endpoint's
      // not-found response as a normal absence so the existing suggestion CTA
      // remains available for them.
      if (r.status === 404) return null;
      if (!r.ok) throw await toApiError(r, 'Failed to load convergence review');
      const body = (await r.json()) as { ok: boolean; data: ConvergenceMergePreview | null };
      return body.data ?? null;
    },
    enabled: isSuperadmin && !!workflowId && !!forkedFromWorkflowId,
    retry: false,
    staleTime: 15_000,
  });

  const mergeToOriginalMutation = useMutation({
    mutationFn: async (expected: {
      expectedDriftHash: string;
      expectedOriginUpdatedAt: string;
      expectedForkUpdatedAt: string;
    }) => {
      if (!workflowId) throw new Error('No workflow ID');
      const r = await fetch(`${API_URL}/workflows/${workflowId}/convergence-merge`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ confirmDrift: true, ...expected }),
      });
      if (!r.ok) throw await toApiError(r, 'Failed to merge convergence fork');
      return r.json();
    },
    onSuccess: async () => {
      toast.success('Merged optimization into the original flow');
      await qc.invalidateQueries({ queryKey: ['workflow-detail', forkedFromWorkflowId] });
      await qc.invalidateQueries({ queryKey: ['workflow-convergence-merge-preview', workflowId] });
      if (forkedFromWorkflowId) {
        navigate({
          to: '/flows/$workflowId',
          params: { workflowId: toUrlId('wf', forkedFromWorkflowId) },
        });
      }
    },
    onError: (err: Error) => toast.error(err.message || 'Could not merge convergence fork'),
  });

  const onMergeToOriginal = React.useCallback(() => {
    const preview = convergencePreviewQ.data;
    if (!preview) return;
    mergeToOriginalMutation.mutate({
      expectedDriftHash: preview.expectedDriftHash,
      expectedOriginUpdatedAt: preview.expectedOriginUpdatedAt,
      expectedForkUpdatedAt: preview.expectedForkUpdatedAt,
    });
  }, [convergencePreviewQ.data, mergeToOriginalMutation.mutate]);

  return React.useMemo(() => {
    if (!forkedFromWorkflowId) return null;
    return {
      originName: forkedFromName,
      onOpenOrigin: handleOpenOrigin,
      onSuggestToOriginal: () => suggestToOriginalMutation.mutate(),
      isSuggestingToOriginal: suggestToOriginalMutation.isPending,
      convergencePreview: convergencePreviewQ.data ?? null,
      convergencePreviewLoading: convergencePreviewQ.isLoading,
      onMergeToOriginal,
      isMergingToOriginal: mergeToOriginalMutation.isPending,
    };
  }, [
    convergencePreviewQ.data,
    convergencePreviewQ.isLoading,
    forkedFromName,
    forkedFromWorkflowId,
    handleOpenOrigin,
    mergeToOriginalMutation.isPending,
    onMergeToOriginal,
    suggestToOriginalMutation.isPending,
    suggestToOriginalMutation.mutate,
  ]);
}
